import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import toast, { Toaster } from 'react-hot-toast';
import { BeatLoader } from 'react-spinners';
import { manualLogout } from '../../utils/handleApiErrors';
import { getAuthToken } from '../../utils/auth';
import config from '../../../config';
import ContentActions from './ContentActions';
import ContentTable from './ContentTable';
import styles from './Content.module.css';

const getArticles = async (publisherId, page, size, monetizeType) => {
  const accessToken = getAuthToken();

  const payload = {
    urlFilter: '',
    monetizeType,
  };

  const response = await fetch(
    `${config.ApiBaseUrl}/publisher/${publisherId}/articles/${page}/${size}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    }
  );
  const json = await response.json();

  const { data, message, success } = json;

  if (!success)
    throw new Error(
      message ?? 'Something went wrong. failed to process request'
    );

  const { totalCount, items } = data;
  const retrievedArticles = items.map((item) => ({
    title: item.title,
    price: item.price,
    tipJarPrice: item.tipJarPriceOptions.prices,
    monetizeType: item.monetizeType,
    isChecked: item.active,
    category: item.category,
    id: item.id,
  }));

  return { totalCount, retrievedArticles };
};

const getCategories = async (publisherId) => {
  const accessToken = getAuthToken();

  const response = await fetch(
    `${config.ApiBaseUrl}/publisher/${publisherId}/articleCategories`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  const json = await response.json();

  const { data, message, success } = json;

  if (!success)
    throw new Error(
      message ?? 'Something went wrong. failed to process request'
    );

  const categoriesMap = new Map();
  // const retrievedCategories = data.articleCategories.map((item) => ({
  //   category: item.category,
  //   id: item.id,
  //   price: item.price,
  // }));

  // for (const category of data.articleCategories) {
  //   categoriesMap.set(category.category, category.id);
  // }
  data.articleCategories.forEach((category) => {
    categoriesMap.set(category.category, category.id);
  });

  return categoriesMap;
};

const putUpsertArticles = async (articles, publisherId, userId) => {
  const accessToken = getAuthToken();

  const payload = {
    articleList: articles,
    publisherId,
    userId,
  };

  const response = await fetch(
    `${config.ApiBaseUrl}/wordpress/upsertArticles`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    }
  );

  const { data, message, success } = await response.json();

  if (!success)
    throw new Error(
      message ?? 'Something went wrong. failed to process request'
    );

  const upsertedArticles = data.map((item) => ({
    title: item.title,
    price: item.price,
    isChecked: item.active,
    category: item.category,
    id: item.id,
  }));

  return upsertedArticles;
};

const createArticlesAndCategories = (
  publisherId,
  page,
  size,
  setArticles,
  setCategories,
  setPagination,
  monetizeType
) => {
  Promise.all([
    getArticles(publisherId, page, size, monetizeType),
    getCategories(publisherId),
  ])
    .then(([articlesData, retrievedCategoriesMap]) => {
      const categories = new Set(
        articlesData.retrievedArticles.map((article) => article.category)
      );
      const categoriesArr = [];

      // for (const category of categories.values()) {
      //   categoriesArr.push({
      //     category,
      //     id: retrievedCategoriesMap.get(category),
      //   });
      // }
      categories.forEach((category) => {
        categoriesArr.push({
          category,
          id: retrievedCategoriesMap.get(category),
        });
      });

      setPagination({
        page,
        totalCount: articlesData.totalCount,
      });
      setArticles(articlesData.retrievedArticles);
      setCategories(categoriesArr);
    })
    .catch((e) => {
      toast.error(`Something went wrong! ${e.message}`, {
        duration: 7000,
      });
    });
};

const Content = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({
    name: 'none',
    id: -1,
  });
  const [syncing, setSyncing] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalCount: null,
  });

  const [selectedMonetizeType, setSelectedMonetizeType] = useState('paywall');

  const publisherId = useSelector((state) => state.user.publisher?.id);
  const userId = useSelector((state) => state.user.user?.id);
  const wpArticles = useSelector((state) => state.wpArticles?.articles);

  useEffect(() => {
    if (publisherId) {
      createArticlesAndCategories(
        publisherId,
        1,
        7,
        setArticles,
        setCategories,
        setPagination,
        selectedMonetizeType
      );
    }
  }, [publisherId, selectedMonetizeType]);

  const history = useHistory();

  const handleLogout = () => {
    manualLogout();
    history.push('/');
  };

  const handleSyncing = async () => {
    setSyncing(true);
    setSelectedCategory({
      name: 'none',
      id: -1,
    });
    toast
      .promise(
        putUpsertArticles(wpArticles, publisherId, userId).then(() => {
          createArticlesAndCategories(
            publisherId,
            1,
            7,
            setArticles,
            setCategories,
            setPagination,
            selectedMonetizeType
          );
        }),
        {
          loading: 'please wait while we sync...',
          success: 'articles syncing successfully!',
          error: 'something went wrong while syncing!',
        }
      )
      .finally(() => {
        setSyncing(false);
      });
  };

  const handlePagination = (type) => {
    setSelectedCategory({
      name: 'none',
      id: -1,
    });

    if (type === 'next') {
      const page = pagination.page + 1;

      createArticlesAndCategories(
        publisherId,
        page,
        7,
        setArticles,
        setCategories,
        setPagination,
        selectedMonetizeType
      );
    }
    if (type === 'prev') {
      const page = pagination.page - 1;

      createArticlesAndCategories(
        publisherId,
        page,
        7,
        setArticles,
        setCategories,
        setPagination,
        selectedMonetizeType
      );
    }
  };

  return (
    <>
      <ContentActions
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        setSelectedMonetizeType={setSelectedMonetizeType}
        selectedMonetizeType={setSelectedMonetizeType}
        categories={categories}
        setArticles={setArticles}
      />
      <ContentTable
        articles={articles}
        selectedCategory={selectedCategory}
        selectedMonetizeType={selectedMonetizeType}
        setArticles={setArticles}
      />
      <div className={`${styles.content__actions}`}>
        <div>
          <button
            type="button"
            onClick={handleLogout}
            className="global__button"
          >
            Logout
          </button>
          <button
            type="button"
            onClick={handleSyncing}
            className="global__button"
          >
            {!syncing && 'Sync'}{' '}
            {syncing && (
              <BeatLoader color="white" loading={syncing} size={5} margin={1} />
            )}
          </button>
        </div>
        <div>
          <button
            type="button"
            onClick={() => {
              handlePagination('prev');
            }}
            className={`${styles['button--type-pagination']}`}
            disabled={pagination.page * 7 <= 7}
          >
            <i className={`material-icons ${styles.pagination__icon}`}>
              chevron_left
            </i>
          </button>
          <button
            type="button"
            onClick={() => {
              handlePagination('next');
            }}
            className={`${styles['button--type-pagination']}`}
            disabled={pagination.page * 7 >= pagination.totalCount}
          >
            <i className={`material-icons  ${styles.pagination__icon}`}>
              chevron_right
            </i>
          </button>
        </div>
      </div>
      <Toaster
        containerStyle={{
          top: 50,
          left: 20,
          bottom: 20,
          right: 20,
        }}
      />
    </>
  );
};

export default Content;
